// rect.ts — the C++ pixel Rect (ht.h:566-640 + misc.cpp:180-381) + the rect-shaped
// RectLike interface. Extracted from form.ts (canvas-removal R3): Rect is foundational
// geometry depended on by code that long outlives the Form mechanism (the renderer, the
// playfield layers, buildui, the input slices, web/main), so it lives in its own neutral
// module. NOT part of the forms.ini Form mechanism — pure value geometry.

import { isqrt } from '../../../sim/src/math/isqrt.ts';

/** rect-shaped: the gfx-layer { left, top, right, bottom } interface. */
export interface RectLike {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// ht.h:566-640 + misc.cpp:180-381 — the pixel rect (structurally compatible
// with the gfx layer's plain Rect interface)
export class Rect {
  left = 0;
  top = 0;
  right = 0;
  bottom = 0;

  // ht.h:611-617
  Set(l: number, t: number, r: number, b: number): void {
    this.left = l;
    this.top = t;
    this.right = r;
    this.bottom = b;
  }

  // C++ struct assignment (*this = *prc)
  SetRc(prc: RectLike): void {
    this.left = prc.left;
    this.top = prc.top;
    this.right = prc.right;
    this.bottom = prc.bottom;
  }

  // ht.h:619-625
  SetEmpty(): void {
    this.left = 0;
    this.right = 0;
    this.bottom = 0;
    this.top = 0;
  }

  // ht.h:627-630
  IsEmpty(): boolean {
    return this.left >= this.right || this.top >= this.bottom;
  }

  // ht.h:631-639
  Width(): number {
    return this.right - this.left;
  }
  Height(): number {
    return this.bottom - this.top;
  }

  // ht.h:585-591
  Offset(dx: number, dy: number): void {
    this.left += dx;
    this.right += dx;
    this.top += dy;
    this.bottom += dy;
  }

  // ht.h:593-599
  Inflate(cx: number, cy: number): void {
    this.left -= cx;
    this.right += cx;
    this.top -= cy;
    this.bottom += cy;
  }

  // ht.h:601-604
  PtIn(x: number, y: number): boolean {
    return x >= this.left && x < this.right && y >= this.top && y < this.bottom;
  }

  // ht.h:606-609
  RectIn(prc: RectLike): boolean {
    return (
      prc.left >= this.left &&
      prc.top >= this.top &&
      prc.right <= this.right &&
      prc.bottom <= this.bottom
    );
  }

  // misc.cpp:180-200
  Intersect(prcSrc1: RectLike, prcSrc2: RectLike): boolean {
    this.left = Math.max(prcSrc1.left, prcSrc2.left);
    this.right = Math.min(prcSrc1.right, prcSrc2.right);

    // Check for empty rect

    if (this.left < this.right) {
      this.top = Math.max(prcSrc1.top, prcSrc2.top);
      this.bottom = Math.min(prcSrc1.bottom, prcSrc2.bottom);

      // Check for empty rect

      if (this.top < this.bottom) return true;
    }

    this.SetEmpty();
    return false;
  }

  // misc.cpp:261-308 — prcSrc1 + prcSrc2 (not Union: only edge-spanning merges)
  Add(prcSrc1: Rect, prcSrc2: RectLike): void {
    if (prcSrc1.IsEmpty()) {
      this.SetRc(prcSrc2);
      return;
    }
    if (prcSrc2.left >= prcSrc2.right || prcSrc2.top >= prcSrc2.bottom) {
      this.SetRc(prcSrc1);
      return;
    }

    let xLeft = prcSrc1.left;
    let yTop = prcSrc1.top;
    let xRight = prcSrc1.right;
    let yBottom = prcSrc1.bottom;

    // Vert edges

    if (prcSrc2.top <= prcSrc1.top && prcSrc2.bottom >= prcSrc1.bottom) {
      // left edge

      if (prcSrc2.left < prcSrc1.left && prcSrc2.right >= prcSrc1.left) xLeft = prcSrc2.left;

      // right edge

      if (prcSrc2.left <= prcSrc1.right && prcSrc2.right > prcSrc1.right) xRight = prcSrc2.right;
    }

    // Horz edges

    if (prcSrc2.left <= prcSrc1.left && prcSrc2.right >= prcSrc1.right) {
      // top edge

      if (prcSrc2.top < prcSrc1.top && prcSrc2.bottom >= prcSrc1.top) yTop = prcSrc2.top;

      // bottom edge

      if (prcSrc2.top <= prcSrc1.bottom && prcSrc2.bottom > prcSrc1.bottom)
        yBottom = prcSrc2.bottom;
    }

    this.Set(xLeft, yTop, xRight, yBottom);
  }

  // misc.cpp:326-339
  Union(prc: RectLike): void {
    if (prc.left >= prc.right || prc.top >= prc.bottom) return;
    if (prc.left < this.left) this.left = prc.left;
    if (prc.top < this.top) this.top = prc.top;
    if (prc.right > this.right) this.right = prc.right;
    if (prc.bottom > this.bottom) this.bottom = prc.bottom;
  }

  // misc.cpp:341-346
  Equal(prc: RectLike): boolean {
    if (
      this.left === prc.left &&
      this.top === prc.top &&
      this.right === prc.right &&
      this.bottom === prc.bottom
    )
      return true;
    return false;
  }

  // misc.cpp:353-381 — distance from a point to the rect (0 inside)
  GetDistance(x: number, y: number): number {
    if (y >= this.bottom) {
      if (x >= this.right) {
        return isqrt(x - this.right, y - this.bottom);
      }
      if (x >= this.left) {
        return y - this.bottom;
      }
      return isqrt(this.left - x, y - this.bottom);
    } else if (y >= this.top) {
      if (x >= this.right) {
        return x - this.right;
      }
      if (x >= this.left) {
        return 0;
      }
      return this.left - x;
    } else {
      if (x >= this.right) {
        return isqrt(x - this.right, this.top - y);
      }
      if (x >= this.left) {
        return this.top - y;
      }
      return isqrt(this.left - x, this.top - y);
    }
  }
}
